// @givmo/compliance — the workbench's page column (CCM-R1-DOCS; slice 2 §5.1 and §5.6, frames
// 31–37): one page of the form as the server draws it for the answers as they stand, the selected
// item's blanks outlined on it, row 7's sentence under it and what Download hands over.
//
// ⚠ THE PAGE IS THE SERVER'S PNG, NEVER A RE-DRAWING. Each page is fetched per state of the answers
// (its query key carries `values_sha256`), so what is looked at is what Download records. The
// outline is placed from the served geometry (CCM-R1-DOCS-READS): the page's crop box, in PDF
// points from its bottom left, maps onto the image as a fraction of the box, so it holds at any
// displayed width; the served draw scale gives the image its intrinsic size. A rotated page is
// drawn without an outline rather than with a wrong one. Once the form is signed (frame 46;
// CCM-R1-SIGN-WEB-B), each page is the signed form's own stored rendition, "as signed", and a final
// composed before those were kept (a 404) shows no picture, never an error. Once a copy signed
// outside Givmo is recorded (frame 87; CCM-R1-SIGN-OUTSIDE-B), each page is the copy's own drawn page,
// counted as the copy counts them, and fitted to the page's box as drawn; a copy another length than
// the form (only its signed page, or a cover sheet) is shown by its own pages, opening on its last.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FOCUS_RING, Skeleton, cn, testIdProp } from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import { ComplianceDisclosure } from './compliance-disclosure';
import type { FormPage, FormPreparation, Rect } from './compliance-preparation-decode';
import type { RenditionPage } from './compliance-signing-decode';
import { DOWNLOAD_AS_SIGNED_NOTE } from './compliance-sending-model';
import { DOWNLOAD_AS_UPLOADED_NOTE, pageOfSignedCopy } from './compliance-signed-outside-model';
import { pageOfLine } from './compliance-signing-model';
import {
  DOWNLOAD_NOTE,
  formPagesOf,
  instructionPagesOf,
  pagesPhrase,
  placeBox,
} from './compliance-preparation-model';
import { formPagePreviewQueryKey } from './compliance-queries';

const PREFIX = 'compliance-workbench-page';
/** The column's own test id: the workbench brings it into view for "See the checklist". */
export const WORKBENCH_PAGES_TEST_ID = PREFIX;

/** The panel's quiet underlined action, as the workbench uses it. */
export function QuietButton({
  children,
  onClick,
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong',
        FOCUS_RING,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/**
 * One page's PNG, as an object URL that lives as long as the page is shown.
 *
 * CCM-R1-WORKBENCH-PREVIEW-SUPERSEDED: one preview of the page out at a time, the newest. The
 * request names no state of the answers (the server draws them as they stand when it serves it), and
 * the server draws even an abandoned request whole, so a state superseded while a preview is out is
 * never asked for: newer answers wait, and once it lands only the newest is asked, if the answers
 * moved. Until the page of the answers as they stand lands, the page shows its skeleton, never an
 * earlier state's, so what is looked at is what Download records: every save moves `values_sha256`
 * (the server's digest carries each answer's sequence), so a page drawn for earlier answers is never
 * current again. Leaving the page aborts what is out and drops its drawing (the server draws it
 * anyway), so coming back with the same answers asks again.
 */
function usePagePreview(preparation: FormPreparation, page: number, wanted: boolean) {
  const { orgRef, operations } = useCompliance();
  const { preparationId, valuesSha256: newest } = preparation;
  // The state of the answers the preview is asked for (the page is fixed: each page is its own).
  const [asked, setAsked] = useState({ preparationId, values: newest });
  const askedValues = asked.preparationId === preparationId ? asked.values : newest;
  const preview = useQuery({
    queryKey: formPagePreviewQueryKey(orgRef, preparationId, page, askedValues),
    queryFn: ({ signal }) =>
      operations.getFormPagePreview(orgRef ?? '', preparationId, page, { signal }),
    enabled: orgRef !== null && wanted,
    // A state is drawn once: no Compliance act's refresh (the fiscal year end saved in place, a copy
    // signed outside Givmo recorded) asks it again, nor holds the newest page behind that re-send.
    staleTime: 'static',
  });
  const out = preview.isFetching;
  useEffect(() => {
    // Another preparation is asked for at once; the same one follows its answers only while no
    // preview is out.
    if (asked.preparationId !== preparationId || (!out && asked.values !== newest)) {
      setAsked({ preparationId, values: newest });
    }
  }, [asked, out, preparationId, newest]);
  const current = askedValues === newest;
  const blob = current ? preview.data : undefined;
  const url = useMemo(() => (blob !== undefined ? URL.createObjectURL(blob) : undefined), [blob]);
  useEffect(
    () => () => {
      if (url !== undefined) URL.revokeObjectURL(url);
    },
    [url],
  );
  return { url, failed: current && preview.isError };
}

/** A page of the form, with the selected item's blanks outlined at their served places. */
export function FormPageImage({
  preparation,
  page,
  outline,
  drawn,
  fit = false,
}: {
  preparation: FormPreparation;
  page: FormPage;
  outline: readonly Rect[];
  /**
   * The signed form's own drawn page, "as signed": its short-lived link, none while it is read, and
   * none at all for a final kept before its pages were. Absent, the page as the answers stand.
   */
  drawn?: { url?: string; pending: boolean; width?: number; height?: number };
  /**
   * The drawn page is a copy's (CCM-R1-SIGN-OUTSIDE-B): the box takes the drawn page's own shape,
   * so a scan another size than the form is never stretched to the form's page.
   */
  fit?: boolean;
}) {
  const preview = usePagePreview(preparation, page.page, drawn === undefined);
  const url = drawn === undefined ? preview.url : drawn.url;
  const failed = drawn === undefined ? preview.failed : !drawn.pending;
  const [x0, y0, x1, y1] = page.cropBox;
  const own = fit && drawn?.width !== undefined && drawn.height !== undefined ? drawn : undefined;
  const width = own?.width ?? Math.round((x1 - x0) * page.scale);
  const height = own?.height ?? Math.round((y1 - y0) * page.scale);
  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border border-border-subtle bg-white shadow-xs"
      style={{
        aspectRatio: own !== undefined ? `${width} / ${height}` : `${x1 - x0} / ${y1 - y0}`,
      }}
      {...testIdProp(`${PREFIX}-${page.page}`)}
    >
      {url !== undefined ? (
        <img
          src={url}
          alt=""
          width={width}
          height={height}
          className="block h-full w-full select-none"
          draggable={false}
          {...testIdProp(`${PREFIX}-${page.page}-image`)}
        />
      ) : (
        !failed && <Skeleton className="h-full w-full" />
      )}
      {outline.map((rect, index) => {
        const box = placeBox(rect, page);
        if (box === undefined) return null;
        return (
          <div
            key={index}
            className="pointer-events-none absolute rounded-[3px] bg-brand-500/10 ring-2 ring-brand-500/70"
            style={{
              left: `${box.left}%`,
              top: `${box.top}%`,
              width: `${box.width}%`,
              height: `${box.height}%`,
            }}
            {...testIdProp(`${PREFIX}-${page.page}-outline`)}
          />
        );
      })}
    </div>
  );
}

/** The signed form's (or a copy's) own drawing of one page, as `FormPageImage` takes it. */
function drawnOf(asSigned: { pages: readonly RenditionPage[]; pending: boolean }, page: number) {
  const drawn = asSigned.pages.find((one) => one.page === page);
  return {
    pending: asSigned.pending,
    ...(drawn !== undefined
      ? { url: drawn.imageUrl, width: drawn.width, height: drawn.height }
      : {}),
  };
}

/**
 * The right column: "Page 4 of 4", the way to the form's other pages and to the agency's own
 * instructions (which stay in the PDF, Q5), the page, row 7's sentence and the download note.
 */
export function WorkbenchPages({
  preparation,
  jurisdiction,
  shownPage,
  onShowPage,
  outline,
  asSigned,
  signedCopy,
  paperRoute,
  instructions: shownInstructions,
  onInstructions,
}: {
  preparation: FormPreparation;
  jurisdiction: string;
  /** The page shown: the selected item's, or the form's first. */
  shownPage: number;
  onShowPage: (page: number) => void;
  /**
   * Whether the agency's instruction pages are shown in place of the one page, where the page
   * holding the column decides it ("See the checklist" shows its one page); else the column's own.
   */
  instructions?: boolean;
  onInstructions?: (shown: boolean) => void;
  /** The selected item's places, by page. */
  outline: ReadonlyMap<number, readonly Rect[]>;
  /** Signed: the signed form's own drawn pages, shown "as signed" (none for a final kept before). */
  asSigned?: { pages: readonly RenditionPage[]; pending: boolean };
  /**
   * A copy signed outside Givmo is recorded: its own drawn pages and how many it has (W24, W25;
   * frame 87).
   */
  signedCopy?: { pages: readonly RenditionPage[]; pending: boolean; count?: number };
  /** The quiet line under the note: "Signed on paper or outside Givmo?" (W10, W11; frame 84). */
  paperRoute?: ReactNode;
}) {
  const formPages = formPagesOf(preparation);
  const instructionPages = instructionPagesOf(preparation);
  const [ownInstructions, setOwnInstructions] = useState(false);
  const instructions = shownInstructions ?? ownInstructions;
  const setInstructions = (shown: boolean) => {
    setOwnInstructions(shown);
    onInstructions?.(shown);
  };
  const total = preparation.pages.length;
  // A copy is counted as the copy counts its pages (a scan may carry a cover sheet).
  const copyCount =
    signedCopy?.count ??
    (signedCopy !== undefined && signedCopy.pages.length > 0 ? signedCopy.pages.length : total);
  // A copy another length than the form (only its signed page, or a cover sheet) is shown page by
  // page as the copy has them: the form's own pages and instructions are not the copy's.
  const ownPages = signedCopy !== undefined && copyCount !== total;
  const heading = (page: number) =>
    signedCopy !== undefined
      ? pageOfSignedCopy(page, copyCount)
      : pageOfLine(page, total, asSigned !== undefined);
  const drawnPages = signedCopy ?? asSigned;
  const pageOf = (number: number) => preparation.pages.find((page) => page.page === number);
  // A copy's page past the form's own keeps the form's first page's box until its drawing is read.
  const copyPageOf = (number: number): FormPage | undefined => {
    const first = preparation.pages[0];
    return pageOf(number) ?? (first !== undefined ? { ...first, page: number } : undefined);
  };
  const copyPage = Math.min(Math.max(shownPage, 1), copyCount);
  const shown = ownPages
    ? [copyPageOf(copyPage)].filter((page): page is FormPage => page !== undefined)
    : instructions
      ? instructionPages.map(pageOf).filter((page): page is FormPage => page !== undefined)
      : [pageOf(shownPage)].filter((page): page is FormPage => page !== undefined);
  const otherPages = ownPages
    ? Array.from({ length: copyCount }, (_, index) => index + 1).filter((page) => page !== copyPage)
    : formPages.filter((page) => instructions || page !== shownPage);

  // The column's heading stays clear of the host's sticky bar, where the column sticks and where the
  // checklist link scrolls to it: the bar's height (`--charity-app-bar-h`, the one host contract this
  // package reads; 0px where a host has no bar), then 24px.
  return (
    <aside
      className="flex min-w-0 scroll-mt-[calc(var(--charity-app-bar-h,0px)+24px)] flex-col gap-12 xl:sticky xl:top-[calc(var(--charity-app-bar-h,0px)+24px)]"
      {...testIdProp(PREFIX)}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-4">
        <h2 className="text-s14 font-w600 text-text-strong" {...testIdProp(`${PREFIX}-heading`)}>
          {heading(shown[0]?.page ?? shownPage)}
        </h2>
        <span className="flex flex-wrap gap-12">
          {otherPages.map((page) => (
            <QuietButton
              key={page}
              onClick={() => {
                setInstructions(false);
                onShowPage(page);
              }}
              testId={`${PREFIX}-show-${page}`}
            >
              {`Show page ${page}`}
            </QuietButton>
          ))}
          {!ownPages && !instructions && instructionPages.length > 0 && (
            <QuietButton onClick={() => setInstructions(true)} testId={`${PREFIX}-instructions`}>
              {formPages.length > 1
                ? `Instructions (${pagesPhrase(instructionPages)})`
                : `Show ${jurisdiction}’s instructions (${pagesPhrase(instructionPages)})`}
            </QuietButton>
          )}
        </span>
      </div>
      {shown.map((page, index) => (
        <div key={page.page} className="flex flex-col gap-8">
          {/* Each further instruction page is named as the first is. */}
          {index > 0 && (
            <h3 className="text-s14 font-w600 text-text-strong">{heading(page.page)}</h3>
          )}
          <FormPageImage
            preparation={preparation}
            page={page}
            outline={instructions || drawnPages !== undefined ? [] : (outline.get(page.page) ?? [])}
            {...(drawnPages !== undefined ? { drawn: drawnOf(drawnPages, page.page) } : {})}
            fit={signedCopy !== undefined}
          />
        </div>
      ))}
      {/* D-CCM-19 §3 row 7, directly under the page (§5.1), never in a document's bytes. */}
      <ComplianceDisclosure surface="filing-workbench" />
      <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-note`)}>
        {signedCopy !== undefined
          ? DOWNLOAD_AS_UPLOADED_NOTE
          : asSigned !== undefined
            ? DOWNLOAD_AS_SIGNED_NOTE
            : DOWNLOAD_NOTE}
      </p>
      {paperRoute}
    </aside>
  );
}
